import { Skeleton } from "@heroui/react";

/**
 * Loading placeholder for the recipe detail page. Mirrors the real layout —
 * HeroPageLayout's split hero (header text + cover image), the stats banner, and
 * the ingredients/directions columns — so the page keeps its shape while the
 * recipe loads and the swap to real content is seamless rather than a jump.
 *
 * The title slot shows a themed message instead of a bar: it's the one spot
 * where a word reads better than a shape.
 */
export const RecipeDetailsSkeleton = () => {
    return (
        <div role="status" aria-live="polite">
            <span className="sr-only">Loading recipe…</span>

            {/* Hero — mirrors HeroPageLayout (header left, cover image right; on
                mobile the image stacks above via flex-wrap-reverse). */}
            <div className="flex justify-center overflow-clip relative max-h-max xl:h-[84vh] xl:max-h-[calc(100vh-64px)]">
                <div className="w-full relative flex gap-y-10 gap-x-0 flex-wrap-reverse xl:flex-nowrap xl:justify-end">
                    {/* Left: header content */}
                    <div className="pl-[20px] basis-full xl:basis-[600px] 2xl:basis-[760px] grow-1 xl:grow-0 w-full self-center pr-[28px] xl:px-[20px] 2xl:pl-[60px] 2xl:pr-[140px]">
                        {/* Recipe-type eyebrow */}
                        <Skeleton aria-hidden className="mb-3 h-3 w-24 rounded" />

                        {/* Title slot — a message rather than a bar */}
                        <p className="font-serif text-4xl xl:text-6xl xl:max-w-[575px] italic text-[var(--fg-muted)] animate-pulse">
                            Conjuring recipe…
                        </p>

                        {/* Chips row */}
                        <div className="flex flex-wrap gap-2 mt-[30px] mb-[20px] max-w-[575px] items-center" aria-hidden>
                            <Skeleton className="h-9 w-9 rounded-full" />
                            <Skeleton className="h-9 w-28 rounded-full" />
                            <Skeleton className="h-9 w-20 rounded-full" />
                            <Skeleton className="h-9 w-24 rounded-full" />
                        </div>

                        {/* Description */}
                        <div className="py-4 xl:mb-8 max-w-[575px] space-y-2.5" aria-hidden>
                            <Skeleton className="h-4 w-full rounded" />
                            <Skeleton className="h-4 w-5/6 rounded" />
                            <Skeleton className="h-4 w-2/3 rounded" />
                        </div>
                    </div>

                    {/* Right: cover image */}
                    <div className="basis-full grow-1 xl:basis-auto xl:grow-0 xl:shrink-0 xl:w-[43vw] self-stretch" aria-hidden>
                        <Skeleton className="w-full h-[450px] min-h-[450px] xl:min-h-0 xl:h-full xl:rounded-bl-md" />
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-4 mt-10 xl:mt-20" aria-hidden>
                {/* Stats banner — keeps the real card + ✦ ornament, stats as shapes */}
                <section className="relative w-full rounded-2xl border border-secondary-200/60 bg-[#d2dde4]/70 px-6 pt-7 pb-6 sm:py-7">
                    <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-[50px] bg-[var(--bg)] px-3 leading-none">
                        <span className="font-display text-accent-600/80 text-sm">✦</span>
                    </div>
                    <div className="mx-auto flex max-w-5xl items-stretch justify-center">
                        {[0, 1].map((i) => (
                            <div
                                key={i}
                                className={[
                                    "flex flex-col items-center gap-2.5 px-8 sm:px-12",
                                    i > 0 ? "border-l border-secondary-300/50" : ""
                                ].join(" ")}
                            >
                                <Skeleton className="size-5 rounded-full" />
                                <Skeleton className="h-9 w-24 rounded-lg" />
                                <Skeleton className="h-2.5 w-14 rounded" />
                            </div>
                        ))}
                    </div>
                </section>

                {/* Two columns — ingredients card (left) + directions (right) */}
                <div className="flex flex-col gap-8 mx-auto max-w-7xl w-full overflow-x-hidden sm:overflow-x-visible">
                    <div className="flex row flex-wrap lg:flex-nowrap w-full gap-4 lg:gap-16">
                        {/* Ingredients */}
                        <div className="w-full lg:basis-2/5 lg:pl-12">
                            <div className="rounded-xl bg-default-100 border border-default-200 px-6 pt-8 pb-6">
                                <Skeleton className="mb-7 h-6 w-40 rounded" />
                                <div className="space-y-4">
                                    {["w-11/12", "w-4/5", "w-full", "w-3/4", "w-5/6", "w-2/3", "w-11/12", "w-1/2"].map(
                                        (w, i) => (
                                            <Skeleton key={i} className={`h-3.5 rounded ${w}`} />
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Directions — numbered steps */}
                        <div className="relative w-full lg:basis-3/5 lg:pr-12">
                            <div className="px-4 py-4 lg:pt-8 lg:pb-0">
                                <Skeleton className="mb-7 h-6 w-44 rounded" />
                                <div className="space-y-6">
                                    {[0, 1, 2, 3, 4].map((i) => (
                                        <div key={i} className="flex gap-4">
                                            <Skeleton className="size-8 shrink-0 rounded-full" />
                                            <div className="flex-1 space-y-2.5 pt-1">
                                                <Skeleton className="h-3.5 w-full rounded" />
                                                <Skeleton className="h-3.5 w-11/12 rounded" />
                                                <Skeleton className="h-3.5 w-3/4 rounded" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
